// GrindCTRL SAR display-only pricing.
//
// Shows estimated SAR prices to Saudi Arabia visitors. Real checkout currency
// stays EGP: Shopify supports true local-currency checkout only via Shopify
// Payments or Adyen, neither available to this store, so this is display-only
// by design — not a workaround for a misconfiguration.
//
// Deliberate boundaries:
//  - Cart IS converted, for consistency with browsing. Shopify checkout is not
//    themeable on this plan, so checkout always renders EGP. A fixed bar used
//    to sit at the bottom of every page stating that and showing the real EGP
//    cart total; the owner asked for it removed (2026-09-09). Nothing warns
//    the buyer about the SAR→EGP switch now — that is a deliberate business
//    decision, not an oversight. `git log` has the bar if it's ever wanted.
//  - Never walks SCRIPT/STYLE/NOSCRIPT/TEXTAREA text. Rewriting a money string
//    inside inline JS would corrupt it. Currently no inline script on this
//    theme emits formatted money, but that is luck, not a guarantee.
//  - Rate and copy come from theme settings, so the owner can update the rate
//    from the admin without a code deploy.
//  - The market-switcher label fix (below) runs on EVERY page, Egypt included
//    — that dropdown shows "Saudi Arabia (EGP ...)" regardless of which site
//    you're currently browsing, so the fix can't be gated to the SA site only.
//    Real price conversion stays SA-only; Egypt must keep showing real EGP.

(function () {
  var host = document.querySelector('gc-sar-display');
  if (!host) return;

  var marketCountry = host.dataset.marketCountry || '';
  var onSaudiSite =
    window.location.pathname.indexOf('/en-sa') === 0 ||
    marketCountry === 'SA' ||
    (window.Shopify && window.Shopify.country === 'SA');

  var egpPerSar = parseFloat(host.dataset.egpPerSar) || 3;

  function toSar(egpAmount) {
    // Round up to the nearest 5 for a clean retail number. Must stay in sync
    // with scripts/apply_sar_prices.py, which prices the real SAR price list.
    return Math.ceil(egpAmount / egpPerSar / 5) * 5;
  }

  // Money formats this theme actually renders: "LE 1,200.00", "E£1,200.00",
  // "EGP 1,200.00" — seen across product pages, cart, and checkout. The price
  // range filter on collection/search pages uses a different money_format
  // ("ج.م 1,200.00", the Arabic EGP symbol) that none of those match — found
  // by walking the live /sa site, not guessed.
  var PRICE_RE = /(LE|E£|EGP|ج\.م)\s?([\d,]+\.\d{2})/g;

  // The market switcher lists "Saudi Arabia (EGP ج.م)" since Saudi Arabia has
  // no real presentment currency configured (it inherits the shop's EGP) —
  // correct data, wrong label. Applies on every page (see boundary note
  // above). Egypt's own entry is left untouched; it should say EGP.
  var LOCALE_LABEL_RE = /Saudi Arabia\s*\(EGP[^)]*\)/g;

  var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1, TITLE: 1 };

  function inSkippedRegion(node) {
    var el = node.parentElement;
    while (el) {
      if (SKIP_TAGS[el.tagName]) return true;
      el = el.parentElement;
    }
    return false;
  }

  function convert(root) {
    if (root.nodeType === 1 && SKIP_TAGS[root.tagName]) return;

    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        if (!node.nodeValue) return NodeFilter.FILTER_SKIP;
        LOCALE_LABEL_RE.lastIndex = 0;
        var matchesLocale = LOCALE_LABEL_RE.test(node.nodeValue);
        var matchesPrice = false;
        if (onSaudiSite) {
          PRICE_RE.lastIndex = 0;
          matchesPrice = PRICE_RE.test(node.nodeValue);
        }
        if (!matchesLocale && !matchesPrice) return NodeFilter.FILTER_SKIP;
        return inSkippedRegion(node) ? NodeFilter.FILTER_SKIP : NodeFilter.FILTER_ACCEPT;
      },
    });

    var nodes = [];
    var n;
    while ((n = walker.nextNode())) nodes.push(n);

    // A converted node no longer matches either regex, so re-scans skip it
    // naturally — no bookkeeping needed.
    nodes.forEach(function (node) {
      var value = node.nodeValue.replace(LOCALE_LABEL_RE, 'Saudi Arabia (SAR)');
      if (onSaudiSite) {
        value = value.replace(PRICE_RE, function (full, label, amount) {
          // egp can legitimately be 0 (the filter's minimum-price bound) —
          // check isNaN, not truthiness, or a real "0.00" never converts.
          var egp = parseFloat(amount.replace(/,/g, ''));
          return isNaN(egp) ? full : toSar(egp) + ' SAR';
        });
      }
      node.nodeValue = value;
    });
  }

  function run() {
    convert(document.body);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Catch AJAX-rendered content (quick view, infinite scroll grids) and
  // widgets that update an existing node's text via characterData instead of
  // inserting new nodes (found live on the price-range filter).
  // ponytail: coalesced with rAF so a burst of mutations does one pass, not
  // one per node — collection pages mutate heavily on scroll.
  var queued = [];
  var scheduled = false;
  function flush() {
    scheduled = false;
    var batch = queued;
    queued = [];
    batch.forEach(convert);
  }
  new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      if (m.type === 'characterData') {
        var el = m.target.parentElement;
        if (el) queued.push(el);
        return;
      }
      m.addedNodes.forEach(function (node) {
        if (node.nodeType === 1) queued.push(node);
      });
    });
    if (queued.length && !scheduled) {
      scheduled = true;
      requestAnimationFrame(flush);
    }
  }).observe(document.body, { childList: true, subtree: true, characterData: true });

  // Safety net: confirmed live that at least one third-party app block (a
  // free-shipping progress widget) updates its own text through a mechanism
  // that fires neither a childList nor a characterData mutation record we
  // can see — root cause not fully pinned down, not worth more time chasing
  // through minified third-party JS. A cheap periodic re-scan catches it and
  // anything else like it regardless of the exact mechanism; converted text
  // no longer matches either regex, so repeat scans do near-zero work.
  // ponytail: up to 1.5s of staleness worst case — tighten only if that ever
  // becomes visibly noticeable, not preemptively.
  setInterval(function () { convert(document.body); }, 1500);
})();
